import React, { useEffect, useState, useReducer } from "react";
import { InputAdornment, TextField, Typography, Button, Theme, TextareaAutosize, Container } from "@mui/material";
import Grid from "@mui/material/Grid2";
import makeStyles from "@mui/styles/makeStyles";
import createStyles from "@mui/styles/createStyles";
import LoadingView from "../Components/LoadingView";
import { EmailTemplateRecord, SettingsRecord } from "../repositories/types";
import { useRepositories } from "../repositories";
import { SettingsContext } from "../contexts/SettingsContext";
import { uploadData } from "aws-amplify/storage";
import { useSnackbar } from "notistack";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import Tooltip from "@mui/material/Tooltip";
import { getCurrentUser } from "aws-amplify/auth";
import SubscriptionBlock from "../Components/SubscriptionBlock";


const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: "flex",
      "flex-direction": "column",
      "align-items": "center",
      margin: "8px",
    },
    buttonContainer: {
      margin: "8px",
    },
  })
);

enum SettingsActions {
  UPDATE = "UPDATE",
  INITIALISE = "INIT",
  MILEAGE = "MILEAGE",
  RESET = "RESET",
  BANK_DETAILS = "BANK",
  COMPANY_DETAILS = "COMPANY",
  EMAIL = "EMAIL",
  EMAILTEMPLATE = "EMAILTEMPLATE",
  SAVE = "SAVE",
}

type Action = {
  type: SettingsActions;
  payload?: any;
};

interface SettingsState extends SettingsRecord {
  hasChanged: boolean;
  mileageString?: string;
  originalSettings?: SettingsRecord;
}

const Settings: React.FC = () => {
  const classes = useStyles();
  const [isLoading, setIsLoading] = useState(true);
  const [customerId, setCustomerId] = useState("")
  const context = React.useContext(SettingsContext);
  const { enqueueSnackbar } = useSnackbar();
  const { settings: settingsRepo } = useRepositories();

  const formatDollarsToCents = (value: string) => {
    value = (value + "").replace(/[^\d.-]/g, "");
    if (value && value.includes(".")) {
      value = value.substring(0, value.indexOf(".") + 3);
    }
    return value ? Math.round(parseFloat(value) * 100) : 0;
  };

  const formatCurrency = (cost: number | undefined | null) => {
    if (!cost) {
      return "";
    }
    return (cost / 100).toFixed(2);
  };

  const initialState: SettingsState = { id: "new", hasChanged: false };
  const settingsReducer = (state: SettingsState, action: Action) => {
    switch (action.type) {
      case SettingsActions.UPDATE:
        return Object.assign({}, state, {
          ...state,
          ...action.payload,
          hasChanged: true,
        });
      case SettingsActions.INITIALISE:
        return Object.assign({}, state, {
          ...state,
          ...action.payload,
          mileageString: formatCurrency(action.payload.mileageCost),
          originalSettings: action.payload,
          hasChanged: false,
        });
      case SettingsActions.MILEAGE:
        return Object.assign({}, state, {
          ...state,
          mileageCost: formatDollarsToCents(action.payload),
          hasChanged: true,
          mileageString: action.payload,
        });
      case SettingsActions.BANK_DETAILS:
        const bankDetails = {
          ...state.bankDetails,
          ...action.payload,
        };
        return Object.assign({}, state, {
          ...state,
          bankDetails: bankDetails,
          hasChanged: true,
        });
      case SettingsActions.EMAIL:
        const emailSettings = {
          ...state.emailSettings,
          ...action.payload,
        };
        return Object.assign({}, state, {
          ...state,
          emailSettings: emailSettings,
          hasChanged: true,
        });
      case SettingsActions.EMAILTEMPLATE:
        let emailTemplates = [...(state.emailTemplates ?? [])];
        let templateIndex = emailTemplates.findIndex(
          (template) => template?.templateName === action.payload.templateName
        );
        if (templateIndex === -1) {
          templateIndex = emailTemplates.length;
        }
        const template = { ...emailTemplates[templateIndex], ...action.payload };
        emailTemplates[templateIndex] = template;
        return Object.assign({}, state, {
          ...state,
          emailTemplates: emailTemplates,
          hasChanged: true,
        });
      case SettingsActions.COMPANY_DETAILS:
        const companyDetails = {
          ...state.companyDetails,
          ...action.payload,
        };
        return Object.assign({}, state, {
          ...state,
          companyDetails: companyDetails,
          hasChanged: true,
        });
      case SettingsActions.RESET:
        return Object.assign({}, state, {
          ...state.originalSettings,
          hasChanged: false,
          mileageString: formatCurrency(state.originalSettings?.mileageCost),
        });
      case SettingsActions.SAVE:
        const newSettings = { ...state };
        //TODO fix this, probably shouldn't update something in here. It shows warning in console when saving settings
        context.updateSettings(newSettings);
        return Object.assign({}, state, {
          ...state,
          originalSettings: state,
          hasChanged: false,
        });
      default:
        return state;
    }
  };

  const [state, dispatch] = useReducer(settingsReducer, initialState);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const [s, cId] = await Promise.allSettled([
          settingsRepo.get(),
          getCurrentUser(),
        ]);

        const settings = s.status === "fulfilled" ? s.value : { id: "new" } as any;
        const userEmail = cId.status === "fulfilled" ? (cId.value?.signInDetails?.loginId ?? "") : "";
        const userId = cId.status === "fulfilled" ? (cId.value?.userId ?? "") : "";

        setCustomerId(userId);

        if (!settings.emailSettings) {
          dispatch({
            type: SettingsActions.INITIALISE,
            payload: {
              ...settings,
              emailSettings: {
                subject: "Latest Invoice",
                defaultText: "Dear [client_name], \n Please find attached your latest invoice.",
                email: userEmail,
              },
            },
          });
        } else {
          dispatch({ type: SettingsActions.INITIALISE, payload: settings });
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchSettings();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedFile = async (event: any) => {
    const file = event.target.files[0];
    if (file.size > 250000) {
      enqueueSnackbar("File too big, logo should be no larger than 2.5MB ", { variant: "error" });
      return;
    }
    try {
      const user = await getCurrentUser();
      await uploadData({
        path: () => `private/${user.userId}/${file.name}`,
        data: file,
      }).result;
      enqueueSnackbar("Logo Uploaded", { variant: "success" });
      dispatch({
        type: SettingsActions.UPDATE,
        payload: { logoKey: file.name },
      });
    } catch (err) {
      enqueueSnackbar("Failed to upload logo", { variant: "error" });
    }
  };

  // useEffect(() => {
  //     setMileage(formatCurrency(settings.mileageCost));
  // }, [settings]);

  return (
    <div>
      {isLoading ? (
        <LoadingView />
      ) : (
        <div className={classes.root}>
          <div style={{ alignSelf: "flex-end" }}>
            <SubscriptionBlock subscription={{ ...state.subscription, customerEmail: state.emailSettings?.email ?? "", customerId: customerId }} />
          </div>
          <div>
            <Typography variant="subtitle1">App Settings</Typography>
            <TextField
              id="mileage-cost"
              label="Mileage (per mile) (required)"
              value={state.mileageString}
              onChange={(e) =>
                dispatch({
                  type: SettingsActions.MILEAGE,
                  payload: e.target.value,
                })
              }
              InputProps={{
                startAdornment: <InputAdornment position="start">£</InputAdornment>,
              }}
            />
          </div>
          <br />
          <br />
          <Container maxWidth="sm">
            <Grid container spacing={1} justifyContent="center">
              <Grid size={{ xs: 12 }}>
                <Typography variant="subtitle1">Company Details (required)</Typography>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  id="company-name"
                  label="Company Name"
                  value={state.companyDetails?.name ?? ""}
                  onChange={(e) =>
                    dispatch({
                      type: SettingsActions.COMPANY_DETAILS,
                      payload: { name: e.target.value },
                    })
                  }
                  fullWidth
                />
              </Grid>
              <Grid size={{ xs: 6 }}>
                <TextField
                  id="company-address"
                  label="Address Line 1"
                  value={state.companyDetails?.address ?? ""}
                  onChange={(e) =>
                    dispatch({
                      type: SettingsActions.COMPANY_DETAILS,
                      payload: { address: e.target.value },
                    })
                  }
                  fullWidth
                />
              </Grid>
              <Grid size={{ xs: 6 }}>
                <TextField
                  id="company-address-line-2"
                  label="Address Line 2"
                  value={state.companyDetails?.addressLine2 ?? ""}
                  onChange={(e) =>
                    dispatch({
                      type: SettingsActions.COMPANY_DETAILS,
                      payload: { addressLine2: e.target.value },
                    })
                  }
                  fullWidth
                />
              </Grid>
              <Grid size={{ xs: 4 }}>
                <TextField
                  id="company-address-town"
                  label="Town"
                  value={state.companyDetails?.town ?? ""}
                  onChange={(e) =>
                    dispatch({
                      type: SettingsActions.COMPANY_DETAILS,
                      payload: { town: e.target.value },
                    })
                  }
                />
              </Grid>
              <Grid size={{ xs: 4 }}>
                <TextField
                  id="company-address-region"
                  label="Region"
                  value={state.companyDetails?.county ?? ""}
                  onChange={(e) =>
                    dispatch({
                      type: SettingsActions.COMPANY_DETAILS,
                      payload: { county: e.target.value },
                    })
                  }
                />
              </Grid>
              <Grid size={{ xs: 4 }}>
                <TextField
                  id="company-address-postcode"
                  label="Post Code"
                  value={state.companyDetails?.postCode ?? ""}
                  onChange={(e) =>
                    dispatch({
                      type: SettingsActions.COMPANY_DETAILS,
                      payload: { postCode: e.target.value },
                    })
                  }
                />
              </Grid>
            </Grid>
          </Container>
          <br />
          <br />
          <Grid container spacing={3}>
            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle1">Invoice Settings</Typography>
              <Typography variant="body1">The following settings are for information on your invoices:</Typography>
            </Grid>
            <Grid size={{ xs: 12 }} spacing={2} container justifyContent="center">
              <Grid >
                <TextField
                  id="invoice-prefix"
                  label="Invoice Prefix"
                  value={state.invoicePrefix}
                  onChange={(e) =>
                    dispatch({
                      type: SettingsActions.UPDATE,
                      payload: { invoicePrefix: e.target.value },
                    })
                  }
                />
              </Grid>
              <Grid >
                <TextField
                  id="invoice-number"
                  label="Next Invoice Number"
                  value={state.invoiceNumber}
                  onChange={(e) =>
                    dispatch({
                      type: SettingsActions.UPDATE,
                      payload: { invoiceNumber: parseInt(e.target.value) },
                    })
                  }
                />
              </Grid>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextareaAutosize
                id="invoice-text"
                value={state.invoiceBlurb || ""}
                placeholder="The text you would like to appear on your invoices"
                minRows={5}
                style={{ width: "40%" }}
                onChange={(e) =>
                  dispatch({
                    type: SettingsActions.UPDATE,
                    payload: { invoiceBlurb: e.target.value },
                  })
                }
              />
            </Grid>
            <Grid size={{ xs: 12 }} style={{ display: "flex", flexDirection: "column" }}>
              <Typography variant="subtitle2">Company Logo</Typography>
              <Typography variant="caption">If no image is provided then your company name will be used</Typography>
              <input
                id="btn-upload"
                name="btn-upload"
                accept="image/*"
                style={{ display: "none" }}
                type="file"
                onChange={selectedFile}
              />
              <Typography variant="caption">Current file: {state.logoKey}</Typography>
              <label htmlFor="btn-upload">
                <Button variant="outlined" component="span">
                  {!state.logoKey ? "Choose File" : "Choose New File"}
                </Button>
              </label>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle2">Bank Details (required)</Typography>
              <Grid size={{ xs: 12 }} container direction="column" spacing={2}>
                <Grid >
                  <TextField
                    id="account-name"
                    label="Account Name"
                    value={state.bankDetails?.accountName ?? ""}
                    onChange={(e) =>
                      dispatch({
                        type: SettingsActions.BANK_DETAILS,
                        payload: { accountName: e.target.value },
                      })
                    }
                  />
                </Grid>
                <Grid >
                  <TextField
                    id="bank-name"
                    label="Bank Name"
                    value={state.bankDetails?.bankName ?? ""}
                    onChange={(e) =>
                      dispatch({
                        type: SettingsActions.BANK_DETAILS,
                        payload: { bankName: e.target.value },
                      })
                    }
                  />
                </Grid>
                <Grid >
                  <TextField
                    id="bank-sort-code"
                    label="Sort Code"
                    value={state.bankDetails?.sortCode ?? ""}
                    onChange={(e) =>
                      dispatch({
                        type: SettingsActions.BANK_DETAILS,
                        payload: { sortCode: e.target.value },
                      })
                    }
                  />
                </Grid>
                <Grid >
                  <TextField
                    id="bank-account-number"
                    label="Account Number"
                    value={state.bankDetails?.accountNumber ?? ""}
                    onChange={(e) =>
                      dispatch({
                        type: SettingsActions.BANK_DETAILS,
                        payload: { accountNumber: e.target.value },
                      })
                    }
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          <br />
          <br />
          <Grid container spacing={3}>
            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle1">E-Mail Settings</Typography>
              <Typography variant="body1">The following settings are for sending invoices by E-Mail:</Typography>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Tooltip title="To change your email address, please contact us">
                <TextField
                  id="email-address"
                  label="Your E-Mail Address"
                  disabled={true}
                  value={state.emailSettings?.email}
                  onChange={(e) =>
                    dispatch({
                      type: SettingsActions.EMAIL,
                      payload: { email: e.target.value },
                    })
                  }
                />
              </Tooltip>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="invoice-email" id="invoice-email">
                  <Typography>Invoice Email</Typography>
                  <Tooltip title="When you create an invoice this email will be sent. It can be overridden when you select to send an invoice.">
                    <HelpOutlineIcon fontSize="small" />
                  </Tooltip>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={3}>
                    <Grid size={{ xs: 12 }}>
                      <TextField
                        id="email-subject"
                        label="E-Mail Subject"
                        value={state.emailSettings?.subject}
                        onChange={(e) =>
                          dispatch({
                            type: SettingsActions.EMAIL,
                            payload: { subject: e.target.value },
                          })
                        }
                      />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <TextareaAutosize
                        id="default-email-text"
                        value={state.emailSettings?.defaultText || ""}
                        placeholder="The text you would like to appear in your E-Mails, you can edit this when sending specific E-Mails"
                        minRows={5}
                        style={{ width: "40%" }}
                        onChange={(e) =>
                          dispatch({
                            type: SettingsActions.EMAIL,
                            payload: { defaultText: e.target.value },
                          })
                        }
                      />
                      <Typography variant="caption" display="block" gutterBottom>
                        Use these tags to insert data into your email:
                      </Typography>
                      <Typography variant="caption" display="block" gutterBottom>
                        [client_name], [client_surname], [invoice_name], [invoice_total], [invoice_label]
                      </Typography>
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="invoice-void" id="invoice-void">
                  <Typography>Void Email Template</Typography>
                  <Tooltip title="When you void an invoice, this email will be sent">
                    <HelpOutlineIcon fontSize="small" />
                  </Tooltip>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={3}>
                    <Grid size={{ xs: 12 }}>
                      <TextField
                        id="email-subject"
                        label="E-Mail Subject"
                        value={state.emailTemplates?.find((t: any): t is EmailTemplateRecord => t?.templateName === "void")?.subject}
                        onChange={(e) =>
                          dispatch({
                            type: SettingsActions.EMAILTEMPLATE,
                            payload: { templateName: "void", includeAttachment: true, subject: e.target.value },
                          })
                        }
                      />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <TextareaAutosize
                        id="default-email-text"
                        value={state.emailTemplates?.find((t: any): t is EmailTemplateRecord => t?.templateName === "void")?.body || ""}
                        placeholder="The text you would like to appear in your E-Mails when notifying a client of a voided invoice"
                        minRows={5}
                        style={{ width: "40%" }}
                        onChange={(e) =>
                          dispatch({
                            type: SettingsActions.EMAILTEMPLATE,
                            payload: { templateName: "void", includeAttachment: true, body: e.target.value },
                          })
                        }
                      />
                      <Typography variant="caption" display="block" gutterBottom>
                        Use these tags to insert data into your email:
                      </Typography>
                      <Typography variant="caption" display="block" gutterBottom>
                        [client_name], [client_surname], [invoice_name], [invoice_total], [invoice_label]
                      </Typography>
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>
            </Grid>
          </Grid>
          <div className={classes.buttonContainer}>
            <Button variant="outlined" color="primary" onClick={() => dispatch({ type: SettingsActions.RESET })}>
              Cancel
            </Button>
            <Button
              variant={state.hasChanged ? "contained" : "outlined"}
              color="primary"
              onClick={() => { dispatch({ type: SettingsActions.SAVE }); settingsRepo.save(state); }}
            >
              Save
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;
